import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthState, LoginCredentials, User, RegisterData } from '../types/auth'
import { apiClient } from '../services/api'
import { useAuditorStore } from './auditorStore'

interface AuthStore extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  getCurrentUser: () => Promise<void>
  refreshToken: () => Promise<boolean>
  clearError: () => void
  setLoading: (loading: boolean) => void
  setToken: (token: string) => void
  setUser: (user: any) => void
}

let refreshPromise: Promise<boolean> | null = null

export const useAuthStore = create<AuthStore>()( 
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null })

          const response = await apiClient.login(credentials)

          // Store tokens in localStorage
          localStorage.setItem('auth_token', response.token)
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token)
          }

          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          // Handle auditor-specific setup
          if (response.user?.user_type === 'auditor' && response.user?.accessible_businesses) {
            const auditorStore = useAuditorStore.getState()
            auditorStore.setIsAuditor(true)

            // Transform accessible_businesses: ensure merchant_id and name fields exist
            const transformedBusinesses = response.user.accessible_businesses.map((business: any) => ({
              ...business,
              // If merchant_id doesn't exist, use id (from JWT payload)
              merchant_id: business.merchant_id || business.id,
              // Use business_name if available, otherwise name
              name: business.business_name || business.name
            }))

            console.log('[AuthStore] Transformed businesses:', transformedBusinesses)
            auditorStore.setAccessibleBusinesses(transformedBusinesses)

            // Set first business as current if available
            if (transformedBusinesses.length > 0) {
              auditorStore.setCurrentBusiness(transformedBusinesses[0])
            }
          }

          // Set up automatic token refresh
          const tokenPayload = JSON.parse(atob(response.token.split('.')[1]))
          const expiresIn = (tokenPayload.exp * 1000) - Date.now() - 60000 // Refresh 1 minute before expiry

          setTimeout(() => {
            get().refreshToken()
          }, Math.max(expiresIn, 30000)) // Min 30 seconds

        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
            isAuthenticated: false,
          })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await apiClient.register(data)
          
          // Store tokens in localStorage
          localStorage.setItem('auth_token', response.token)
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token)
          }
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          // Set up automatic token refresh
          const tokenPayload = JSON.parse(atob(response.token.split('.')[1]))
          const expiresIn = (tokenPayload.exp * 1000) - Date.now() - 60000 // Refresh 1 minute before expiry
          
          setTimeout(() => {
            get().refreshToken()
          }, Math.max(expiresIn, 30000)) // Min 30 seconds

        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
            isAuthenticated: false,
          })
          throw error
        }
      },

      logout: () => {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        refreshPromise = null

        // Clear auditor data if applicable
        const auditorStore = useAuditorStore.getState()
        auditorStore.clearAuditorData()

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        })
      },

      getCurrentUser: async () => {
        const token = localStorage.getItem('auth_token')
        if (!token) return

        try {
          set({ isLoading: true })
          
          // Check if token is expired
          const tokenPayload = JSON.parse(atob(token.split('.')[1]))
          const isExpired = tokenPayload.exp * 1000 <= Date.now()
          
          if (isExpired) {
            const refreshSuccess = await get().refreshToken()
            if (!refreshSuccess) {
              get().logout()
              set({ isLoading: false })
              return
            }
          }

          const response = await apiClient.getCurrentUser()

          set({
            user: response.user,
            token,
            isAuthenticated: true,
            isLoading: false,
          })

          // Handle auditor-specific setup
          if (response.user?.user_type === 'auditor' && response.user?.accessible_businesses) {
            const auditorStore = useAuditorStore.getState()
            auditorStore.setIsAuditor(true)

            // Transform accessible_businesses: ensure merchant_id and name fields exist
            const transformedBusinesses = response.user.accessible_businesses.map((business: any) => ({
              ...business,
              // If merchant_id doesn't exist, use id (from JWT payload)
              merchant_id: business.merchant_id || business.id,
              // Use business_name if available, otherwise name
              name: business.business_name || business.name
            }))

            console.log('[AuthStore getCurrentUser] Transformed businesses:', transformedBusinesses)
            auditorStore.setAccessibleBusinesses(transformedBusinesses)

            // Set first business as current if available and none is set
            if (transformedBusinesses.length > 0 && !auditorStore.currentBusiness) {
              auditorStore.setCurrentBusiness(transformedBusinesses[0])
            }
          }
        } catch (error) {
          // Try to refresh token on error
          const refreshSuccess = await get().refreshToken()
          if (!refreshSuccess) {
            get().logout()
          }
          set({ isLoading: false })
        }
      },

      refreshToken: async (): Promise<boolean> => {
        // Prevent multiple simultaneous refresh attempts
        if (refreshPromise) {
          return refreshPromise
        }

        refreshPromise = (async () => {
          try {
            const refreshToken = localStorage.getItem('refresh_token')
            if (!refreshToken) {
              return false
            }

            const response = await apiClient.refreshToken(refreshToken)
            
            localStorage.setItem('auth_token', response.token)
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token)
            }

            set({
              token: response.token,
              user: response.user,
              isAuthenticated: true,
              error: null,
            })

            // Schedule next refresh
            const tokenPayload = JSON.parse(atob(response.token.split('.')[1]))
            const expiresIn = (tokenPayload.exp * 1000) - Date.now() - 60000
            
            setTimeout(() => {
              get().refreshToken()
            }, Math.max(expiresIn, 30000))

            return true
          } catch (error) {
            console.error('Token refresh failed:', error)
            return false
          } finally {
            refreshPromise = null
          }
        })()

        return refreshPromise
      },

      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setToken: (token: string) => {
        localStorage.setItem('auth_token', token)
        set({ token, isAuthenticated: true })
      },

      setUser: (user: any) => {
        set({ user })
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)